import React, { useState, useEffect, useCallback } from 'react';
import { getGallery } from '../api';

export default function GalleryPage() {
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  const loadImages = useCallback(() => {
    setLoading(true);
    const season = filter === 'all' ? null : filter;
    getGallery(season)
      .then((data) => setImages(data))
      .catch(() => setImages([]))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // Close lightbox on Escape key
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') setLightbox(null);
    }
    if (lightbox) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightbox]);

  const filters = [
    { value: 'all', label: 'All' },
    { value: 'summer', label: 'Summer' },
    { value: 'winter', label: 'Winter' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)] via-[var(--color-primary-light,var(--color-primary))] to-[var(--color-text)] opacity-95" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            Gallery
          </h1>
          <p className="mt-6 text-lg text-gray-200 max-w-2xl mx-auto">
            See our mobile sauna in action across the seasons.
          </p>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-20 bg-[var(--color-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Buttons */}
          <div className="flex justify-center gap-3 mb-12">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  filter === f.value
                    ? 'bg-[var(--color-accent)] text-white shadow-md'
                    : 'bg-[var(--color-bg-alt)] text-[var(--color-text)] hover:bg-[var(--color-accent)] hover:text-white'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
              <p className="mt-4 text-[var(--color-text-light)]">Loading gallery...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && images.length === 0 && (
            <div className="text-center py-16">
              <svg className="w-16 h-16 mx-auto text-[var(--color-text-light)] opacity-40 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
              <p className="text-[var(--color-text-light)] text-lg">No images to show yet. Check back soon!</p>
            </div>
          )}

          {/* Image Grid */}
          {!loading && images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((image) => (
                <button
                  key={image.id}
                  onClick={() => setLightbox(image)}
                  className="group relative aspect-square overflow-hidden rounded-xl bg-[var(--color-bg-alt)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-2"
                >
                  <img
                    src={image.url || image.image_url}
                    alt={image.alt_text || image.caption || 'Skaha Sauna'}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-end">
                    {(image.caption || image.alt_text) && (
                      <p className="w-full p-4 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-black/60 to-transparent">
                        {image.caption || image.alt_text}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors text-white"
            aria-label="Close lightbox"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div
            className="max-w-5xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.url || lightbox.image_url}
              alt={lightbox.alt_text || lightbox.caption || 'Skaha Sauna'}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
            {(lightbox.caption || lightbox.alt_text) && (
              <p className="text-white text-center mt-4 text-sm">
                {lightbox.caption || lightbox.alt_text}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
